import { BASE_URL } from '@avanta/shared/constants/common';
import { service } from '@avanta/shared/services/api';
import { AppointmentService as SharedAppointmentService } from '@avanta/shared/services/appointments';
import { type Treatment } from '@avanta/shared/types/appointments';
import { type AxiosResponse } from 'axios';
import {
  type Appointment,
  type CancelAppointmentPayload,
  type Center,
  type GetEnterpriseAppointmentsParams,
  type SessionDetails,
  type UploadDocumentPayload,
} from 'types/appointments';
import { toDocumentFormData } from 'utils/storage';

const API_URL = `${BASE_URL}appointments`;

export type AppointmentServiceTypes = {
  getAppointments: (
    patientId: string,
    psychologistId?: string
  ) => Promise<AxiosResponse<Appointment[]>>;
  cancel: ({
    psychologistId,
    appointmentId,
    patientId,
  }: CancelAppointmentPayload) => Promise<AxiosResponse<void>>;
  getSessionDetails: (sessionId: string) => Promise<AxiosResponse<SessionDetails>>;
  getCenterDetails: (centerId: number) => Promise<AxiosResponse<Center>>;
  getAppointmentsHistory: (patientId: string) => Promise<AxiosResponse<Appointment[]>>;
  getEnterpriseAppointments: (
    enterpriseId: string,
    params?: GetEnterpriseAppointmentsParams
  ) => Promise<AxiosResponse<Appointment[]>>;
  getTreatments: (psychologistId?: string) => Promise<AxiosResponse<Treatment[]>>;
  uploadDocument: (payload: UploadDocumentPayload) => Promise<void>;
};

export const AppointmentService: AppointmentServiceTypes = {
  getAppointments: SharedAppointmentService.getAppointments,
  cancel: SharedAppointmentService.cancel,
  getSessionDetails: SharedAppointmentService.getSessionDetails,
  getCenterDetails: SharedAppointmentService.getCenterDetails,
  getAppointmentsHistory: async patientId =>
    service.get(`${API_URL}/patients/${patientId}/confirmed`),
  getEnterpriseAppointments: async (enterpriseId, params) =>
    service.get(`${API_URL}/enterprises/${enterpriseId}`, { params }),
  getTreatments: async psychologistId =>
    service.get(`${API_URL}/insurance/treatments`, {
      params: { psychologistId },
    }),
  uploadDocument: async ({ appointmentId, document }) => {
    await service.post(`${API_URL}/${appointmentId}/documents`, toDocumentFormData(document), {
      headers: { 'Content-Type': false },
    });
  },
};
